<template>
  <div>
    <popup-hint hint="stills">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Stills">
          <template v-slot:left v-if="maintenanceDueCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{maintenanceDueCount}} still{{maintenanceDueCount>1?'s':''}} due for maintenance
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';;filters.maintenanceDue='1'})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Stills are required in order to distill.
      </template>
      <template v-slot:bottom>
        You can create stills individually or do a batch import of stills.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'EquipmentStillsEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="stills" :fields="importFields" importAction="stillsImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="stills" objectType="stills" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="stills2" v-model="selected" @list="listBuilt"
                   fields="id,name,site{id,name},active,pictureURL,manufacturer,modelNumber,serialNumber,maintenanceDue"
                   :listFilters="listFilters"
                   @dblclick="still => $router.push({name:'EquipmentStillsEdit',params:{stillId:still.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   :item-class="still => (still.active ? '' : 'grey--text')"
                   title="Stills"
                   :print-info="{subTitle:[filters.maintenanceDue=='1'?'Maintenance due':'',filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                           filters.siteId!=null?siteName:'']}"
                   download-endpoint="stillsExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.maintenanceDue='0';filters.id=null">
          <v-list-item>
            <filter-search label="Name" v-model="filters.search"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.maintenanceDue" true-value="1" false-value="0" label="Maintenance due"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id" close small color="primary" class="ml-1" @click:close="filters.id=null">Scanned still</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Still: {{filters.search}}</v-chip>
          <v-chip v-show="filters.maintenanceDue=='1'" close small class="ml-1" @click:close="filters.maintenanceDue='0'">Maintenance due</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'EquipmentStillsEditNew'}"><v-list-item-title>Create still</v-list-item-title></v-list-item>
          <v-list-item @click="doImport=true" v-if="!$vuetify.breakpoint.mobile"><v-list-item-title>Import stills from CSV</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <equipment-stills-batch-edit :stills="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'EquipmentStillsEdit',params:{stillId:item.id}})"
              :class="{'text-decoration-underline':hover,'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</a>
          </v-hover>
          <div v-else :class="{'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</div>
          <div v-if="item.maintenanceDue" class="grey--text text-caption">
            <v-btn plain color="error" x-small :to="{name:'EquipmentMaintenance',params:{equipmentId:item.id}}" class="px-0">
              Maintenance due
            </v-btn>
          </div>
          <div v-else-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.maintenanceDue="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'EquipmentStillsEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}" v-if="!readOnly">
        <v-menu :nudge-width="220" offset-y :nudge-left="150">
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Still operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template v-if="company.allowAudit && company.permissions.includes('audit')">
              <v-list-item :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
              <v-divider/>
            </template>
            <v-menu :nudge-left="240">
              <template v-slot:activator="{on:reduce}">
                <v-list-item v-on="reduce">
                  <v-list-item-title>Maintenance</v-list-item-title>
                  <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
                </v-list-item>
              </template>
              <v-list dense>
                <v-list-item v-if="!readOnly" :to="{name:'EquipmentMaintenance',params:{equipmentId:item.id}}"><v-list-item-title>Perform maintenance</v-list-item-title></v-list-item>
                <v-list-item :to="{name:'EquipmentMaintenanceLog',params:{equipmentId:item.id}}"><v-list-item-title>Maintenance history</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
            <v-divider/>
            <generate-qr-code type="Still" :id="item.id"/>
            <v-list-item :to="{name:'EquipmentStillsEdit',params:{stillId:item.id}}"><v-list-item-title>Edit still</v-list-item-title></v-list-item>
            <v-list-item @click="stillToDelete=item;showDelete=true"><v-list-item-title>Delete still</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <equipment-list-expand :width="headers.length" :equipment="item"/>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="stillToDelete.name" @delete="performDeleteStill"/>
  </div>
</template>

<script>
import EquipmentListExpand from '@/views/equipment/EquipmentListExpand'
import EquipmentStillsBatchEdit from '@/views/equipment/stills/EquipmentStillsBatchEdit'
import generateQrCode from '@/components/GenerateQrCode'

export default {
  name: 'EquipmentStills',
  components: {EquipmentListExpand,EquipmentStillsBatchEdit,generateQrCode},
  data() {
    return {
      maintenanceDueCount: 0,
      showStatus: false,
      filters: {
        maintenanceDue: '0',
        status: '1',
        siteId: null,
        search: '',
        id: null
      },
      stillToDelete: {},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true}],
      instructions: `<ol class="mb-4">
                      <li>Name</li>
                      </ol>`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'site.name', visible:true},
                       { value: 'maintenanceDue', visible:false}],
      headers: [{ text: 'STILL', value: 'name', width:'49%',sortable:true},
                { text: 'MAINTENANCE', value: 'maintenanceDue', width:'15%',sortable:true},
                { text: 'SITE', value: 'site.name', width:'45%',sortable:true,omit:() => this.sites.length <= 1}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true, {B266:true});
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.maintenanceDue == '1')
        filters.push({field:'maintenanceDue', comparator:'ne', value:'null'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});

      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});

      if (this.filters.search)
        filters.push({field:'name', comparator:'contains', value:this.filters.search});

      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');

    // Maintenance due?
    const results = await this.$apiCall(`query EquipmentStills {
                                            equipmentMaintenanceDue{typeName}
                                          }`,
                                      {});
    if (!results.error)
      this.maintenanceDueCount = results.data.equipmentMaintenanceDue.filter(e => e.typeName === 'Still').length;

    if (!['EquipmentStillsEdit', 'EquipmentStillsEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.maintenanceDueCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    scan({type, id}) {
      if (type === 'Still')
        Object.assign(this.filters, {siteId:null,status:'2',maintenanceDue:'0',id});
    },
    listBuilt({list}) {
      if (this.filters.id && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    async expand(item, value) {
      if (value && !item.maintenance) {
        this.loading = true;
        const results = await this.$apiCall(`query EquipmentStills($id:ID!) {
                                                  still(id:$id) {
                                                    maintenance {frequency,frequencyUnits,nextDate,description}
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'maintenance', results.data.still.maintenance);
        this.loading = false;
      }
    },
    async performDeleteStill() {
      this.loading = true;
      const results = await this.$apiCall(`mutation EquipmentStills($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.stillToDelete.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted still ${this.stillToDelete.name}`});
        this.$refs.table.buildList();
      }
    }
  }
}
</script>